import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  ShieldCheck,
  UserPlus,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Loader2,
  ExternalLink,
  KeyRound,
  Pencil,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const OPS_DASHBOARD_URL = "https://ops-dashboard-136.emergent.host";

const AdminOpsSupervisorsTab = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removingMatricula, setRemovingMatricula] = useState(null);
  const [matriculaInput, setMatriculaInput] = useState("");
  const [lastReport, setLastReport] = useState(null);
  // Credentials dialog state
  const [credDialog, setCredDialog] = useState(null); // {matricula, nome, ops_username}
  const [credUser, setCredUser] = useState("");
  const [credPass, setCredPass] = useState("");
  const [savingCred, setSavingCred] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ops-supervisors`, {
        headers: authHeaders,
      });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error("Falha ao carregar lista de supervisores");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleAdd = async () => {
    const value = matriculaInput.trim();
    if (!value) {
      toast.error("Informe ao menos uma matrícula");
      return;
    }
    setAdding(true);
    try {
      const res = await axios.post(
        `${API}/admin/ops-supervisors`,
        { matriculas: [value] },
        { headers: authHeaders },
      );
      const data = res.data || {};
      setLastReport(data);
      const added = data.stats?.added || 0;
      const notFound = data.stats?.not_found || 0;
      const already = data.stats?.already || 0;

      if (added > 0) {
        toast.success(`${added} supervisor(es) adicionado(s).`);
      }
      if (notFound > 0) {
        toast.error(`Matrícula(s) não encontrada(s): ${(data.not_found || []).join(", ")}`);
      }
      if (already > 0 && added === 0) {
        toast.info(`Já estava(m) cadastrado(s): ${(data.already || []).join(", ")}`);
      }
      setMatriculaInput("");
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao cadastrar supervisor");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (matricula) => {
    if (!window.confirm(`Remover supervisor ${matricula} do painel de operações?`)) {
      return;
    }
    setRemovingMatricula(matricula);
    try {
      await axios.delete(`${API}/admin/ops-supervisors/${matricula}`, {
        headers: authHeaders,
      });
      toast.success(`Matrícula ${matricula} removida.`);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao remover");
    } finally {
      setRemovingMatricula(null);
    }
  };

  const openCredDialog = (item) => {
    setCredDialog(item);
    setCredUser(item.ops_username || "");
    setCredPass("");
  };

  const handleSaveCred = async () => {
    if (!credDialog) return;
    const trimmedUser = credUser.trim();
    if (trimmedUser && !credPass) {
      toast.error("Informe a senha (ou limpe o usuário para remover credencial)");
      return;
    }
    setSavingCred(true);
    try {
      await axios.put(
        `${API}/admin/ops-supervisors/${credDialog.matricula}/credentials`,
        { ops_username: trimmedUser, ops_password: credPass },
        { headers: authHeaders },
      );
      toast.success(
        trimmedUser
          ? "Credenciais Ops salvas — SSO habilitado."
          : "Credenciais removidas — login manual no Ops.",
      );
      setCredDialog(null);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar credenciais");
    } finally {
      setSavingCred(false);
    }
  };

  const handleClearCred = async () => {
    if (!credDialog) return;
    if (!window.confirm("Remover credenciais e voltar para login manual no Ops?")) {
      return;
    }
    setSavingCred(true);
    try {
      await axios.put(
        `${API}/admin/ops-supervisors/${credDialog.matricula}/credentials`,
        { ops_username: "", ops_password: "" },
        { headers: authHeaders },
      );
      toast.success("Credenciais Ops removidas.");
      setCredDialog(null);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao limpar");
    } finally {
      setSavingCred(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ops-supervisors-tab">
      {/* Header / explanation */}
      <Card className="bg-gradient-to-r from-indigo-50 to-violet-50 border-indigo-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-indigo-900">
            <ShieldCheck className="w-5 h-5" />
            Supervisores — Painel de Operações
          </CardTitle>
          <CardDescription className="text-indigo-800/80">
            Funcionários listados aqui terão a aba <b>“Supervisão Operacional”</b>{" "}
            liberada no app, com acesso ao painel externo de lançamentos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <a
            href={OPS_DASHBOARD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-indigo-700 hover:text-indigo-900 underline-offset-4 hover:underline"
            data-testid="ops-dashboard-external-link"
          >
            Abrir painel externo
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </CardContent>
      </Card>

      {/* Add form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserPlus className="w-4 h-4 text-emerald-600" />
            Adicionar supervisor
          </CardTitle>
          <CardDescription>
            Digite a matrícula do funcionário. Você pode colar várias separadas
            por vírgula, espaço ou ponto-e-vírgula.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Label htmlFor="sup-matricula" className="sr-only">
                Matrícula(s)
              </Label>
              <Input
                id="sup-matricula"
                value={matriculaInput}
                onChange={(e) => setMatriculaInput(e.target.value)}
                placeholder="Ex.: 02984, 03210, 01637"
                disabled={adding}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAdd();
                }}
                data-testid="ops-supervisor-matricula-input"
              />
            </div>
            <Button
              onClick={handleAdd}
              disabled={adding || !matriculaInput.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="ops-supervisor-add-btn"
            >
              {adding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Adicionar
            </Button>
          </div>

          {lastReport && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
              {lastReport.added?.length > 0 && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2">
                  <p className="font-semibold text-emerald-800 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Adicionados ({lastReport.added.length})
                  </p>
                  <ul className="mt-1 text-emerald-700">
                    {lastReport.added.map((it) => (
                      <li key={it.matricula}>
                        {it.matricula} — {it.nome}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {lastReport.already?.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2">
                  <p className="font-semibold text-amber-800">
                    Já cadastrados ({lastReport.already.length})
                  </p>
                  <ul className="mt-1 text-amber-700 font-mono">
                    {lastReport.already.join(", ")}
                  </ul>
                </div>
              )}
              {lastReport.not_found?.length > 0 && (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-2">
                  <p className="font-semibold text-rose-800 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Matrículas inexistentes ({lastReport.not_found.length})
                  </p>
                  <ul className="mt-1 text-rose-700 font-mono">
                    {lastReport.not_found.join(", ")}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base">
              Supervisores ativos
              <Badge
                variant="secondary"
                className="ml-2"
                data-testid="ops-supervisors-count"
              >
                {items.length}
              </Badge>
            </CardTitle>
            <CardDescription>
              Esta lista define quem enxerga a aba “Supervisão Operacional” no
              app do funcionário.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Carregando…
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 p-8 text-center text-slate-500 text-sm">
              Nenhum supervisor cadastrado ainda.
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Matrícula</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead className="w-[140px]">Acesso ao Ops</TableHead>
                    <TableHead className="w-[150px]">Cadastrado em</TableHead>
                    <TableHead className="w-[120px] text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.matricula} data-testid={`ops-sup-row-${it.matricula}`}>
                      <TableCell className="font-mono">{it.matricula}</TableCell>
                      <TableCell className="font-medium">{it.nome || "—"}</TableCell>
                      <TableCell>
                        {it.has_credentials ? (
                          <Badge
                            className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100"
                            data-testid={`ops-sup-sso-yes-${it.matricula}`}
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            SSO ativo
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="border-amber-300 text-amber-700"
                            data-testid={`ops-sup-sso-no-${it.matricula}`}
                          >
                            <KeyRound className="w-3 h-3 mr-1" />
                            Login manual
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {it.added_at
                          ? new Date(it.added_at).toLocaleString("pt-BR")
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openCredDialog(it)}
                            className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                            data-testid={`ops-sup-cred-${it.matricula}`}
                            title={it.has_credentials ? "Editar credenciais Ops" : "Cadastrar credenciais Ops para SSO"}
                          >
                            {it.has_credentials ? (
                              <Pencil className="w-4 h-4" />
                            ) : (
                              <KeyRound className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemove(it.matricula)}
                            disabled={removingMatricula === it.matricula}
                            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            data-testid={`ops-sup-remove-${it.matricula}`}
                          >
                            {removingMatricula === it.matricula ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Credentials dialog */}
      <Dialog
        open={!!credDialog}
        onOpenChange={(open) => !open && setCredDialog(null)}
      >
        <DialogContent className="sm:max-w-md" data-testid="ops-cred-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-indigo-600" />
              Credenciais do Ops Dashboard
            </DialogTitle>
            <DialogDescription>
              {credDialog?.nome || ""} (matrícula {credDialog?.matricula || "—"}).
              Ao salvar, o supervisor entrará automaticamente no painel sem
              digitar nada.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="ops-cred-user">Usuário no Ops (Chapa)</Label>
              <Input
                id="ops-cred-user"
                value={credUser}
                onChange={(e) => setCredUser(e.target.value)}
                placeholder="Ex.: 02984 ou nome cadastrado no Ops"
                autoComplete="off"
                data-testid="ops-cred-user"
              />
            </div>
            <div>
              <Label htmlFor="ops-cred-pass">Senha no Ops</Label>
              <Input
                id="ops-cred-pass"
                type="password"
                value={credPass}
                onChange={(e) => setCredPass(e.target.value)}
                placeholder={
                  credDialog?.has_credentials
                    ? "Digite uma nova senha (mantém anterior se em branco)"
                    : "Digite a senha do Ops"
                }
                autoComplete="off"
                data-testid="ops-cred-pass"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                A senha é criptografada antes de ser salva no banco.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            {credDialog?.has_credentials && (
              <Button
                variant="ghost"
                onClick={handleClearCred}
                disabled={savingCred}
                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 mr-auto"
                data-testid="ops-cred-clear"
              >
                Remover credenciais
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setCredDialog(null)}
              disabled={savingCred}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveCred}
              disabled={
                savingCred ||
                !credUser.trim() ||
                (!credPass && !credDialog?.has_credentials)
              }
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="ops-cred-save"
            >
              {savingCred ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOpsSupervisorsTab;
